import GroupIcon from '@mui/icons-material/Group';
import GroupAddIcon from '@mui/icons-material/GroupAdd';
import SendIcon from '@mui/icons-material/Send';
import Grid from '@mui/material/Grid';
import IconButton from '@mui/material/IconButton';
import { Theme } from '@mui/material/styles';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import * as React from 'react';
import { makeStyles } from 'tss-react/mui';
import DialogCreateGroupedContacts from '../../../components/Contact/ContactGroups/DialogCreateGroupedContacts/DialogCreateGroupedContacts';
import DialogAddGroupContacts from '../../../components/Contact/DialogAddGroupContacts/DialogAddGroupContacts';
import { NavLinkIconButtonWithRef } from '../../../components/Navigation/NavLinkIconButton/NavLinkIconButton';
import withGridMassAction from '../../../HOCs/withGridMassAction';
import { communicatorRoute } from '../../../routes';
import { ApSchedulerRouteState } from '../../../sic-lib/types/ApSchedulerRouteState';
import { useAppSelector } from '../../../store/hooks';

const useStyles = makeStyles()((theme: Theme) => ({
    root: {
        height: 'inherit',
        padding: theme.spacing(1),
        backgroundColor: theme.palette.secondary.light,
    },
    flexWrapper: {
        height: 'inherit',
    },
    button: {
        padding: '10px',
    },
}));

enum MassActionDialogs {
    none = -1,
    createGroup = 0,
    addGroup,
}

const MassActionCreateContactGroup = withGridMassAction(DialogCreateGroupedContacts);
const MassActionAddGroupContacts = withGridMassAction(DialogAddGroupContacts);

interface Props {
    listId: number;
    selectAll: boolean;
    selected: number[];
}

export default function HeaderMassActions({ listId, selectAll, selected }: Props) {
    const { classes } = useStyles();

    const contactCount = useAppSelector(state => {
        return state.conversationGrid?.contactCount || 0;
    });

    const [selectedCount, setSelectedCount] = React.useState<number>(0);

    React.useEffect(() => {
        const numSelected = selectAll ? contactCount - selected.length : selected.length;

        setSelectedCount(numSelected);
    }, [selectAll, selected, contactCount]);

    const [openDialog, setOpenDialog] = React.useState<MassActionDialogs>(MassActionDialogs.none);

    const handleOpenDialog = (event: React.MouseEvent<HTMLElement>) => {
        const { dialogId } = event.currentTarget.dataset;

        if (dialogId) {
            setOpenDialog(+dialogId as MassActionDialogs);
        }
    };

    const handleCloseDialog = () => {
        setOpenDialog(MassActionDialogs.none);
    };

    return (
        <React.Fragment>
            <div className={classes.root}>
                <Grid
                    container
                    direction="row"
                    wrap="wrap"
                    alignItems="center"
                    spacing={2}
                    className={classes.flexWrapper}
                >
                    <Grid item xs={true}>
                        <Typography component="span" variant="subtitle2">
                            {`Selected: ${selectedCount}`}
                        </Typography>
                    </Grid>
                    {process.env.NODE_ENV === 'development' && listId > -2 && (
                        <Grid item xs="auto">
                            <Tooltip title="Message Scheduler">
                                <NavLinkIconButtonWithRef
                                    pathname={communicatorRoute}
                                    state={
                                        {
                                            apContactIds: selected,
                                            apListId: selectAll ? listId : undefined,
                                            apRouteFrom: 'conversationGrid',
                                        } as ApSchedulerRouteState
                                    }
                                    className={classes.button}
                                >
                                    <SendIcon />
                                </NavLinkIconButtonWithRef>
                            </Tooltip>
                        </Grid>
                    )}
                    <Grid item xs="auto">
                        <Tooltip title="Create Contact Group">
                            <IconButton
                                data-dialog-id={MassActionDialogs.createGroup}
                                onClick={handleOpenDialog}
                                className={classes.button}
                                size="large"
                            >
                                <GroupIcon />
                            </IconButton>
                        </Tooltip>
                    </Grid>
                    <Grid item xs="auto">
                        <Tooltip title="Add Group Contacts">
                            <IconButton
                                data-dialog-id={MassActionDialogs.addGroup}
                                onClick={handleOpenDialog}
                                className={classes.button}
                                size="large"
                            >
                                <GroupAddIcon />
                            </IconButton>
                        </Tooltip>
                    </Grid>
                </Grid>
            </div>
            {
                /* 
                    TIP: dialogs do not unmount when closed(?) presevering state: force 
                    an unmount with conditional rendering 
                 */
                openDialog === MassActionDialogs.addGroup && (
                    <MassActionAddGroupContacts
                        isOpen={openDialog === MassActionDialogs.addGroup}
                        contactIds={selected}
                        onDoneHandler={handleCloseDialog}
                    />
                )
            }
            {openDialog === MassActionDialogs.createGroup && (
                <MassActionCreateContactGroup
                    isOpen={openDialog === MassActionDialogs.createGroup}
                    contactIds={selected}
                    onDoneHandler={handleCloseDialog}
                />
            )}
        </React.Fragment>
    );
}
